import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Dropdown, type MenuProps } from 'antd';
import clsx from 'clsx';
import {
  Children,
  cloneElement,
  type HTMLAttributes,
  isValidElement,
  type KeyboardEvent,
  type PointerEvent,
  type ReactElement,
  type RefAttributes,
} from 'react';
import { PressRipple } from '@/components/PressRipple';
import { useShellText } from '@/hooks/useShellText';

type SortableTabProps = {
  className?: string;
  draggingClassName?: string;
  node: ReactElement<HTMLAttributes<HTMLDivElement> & RefAttributes<HTMLDivElement>>;
  menu: MenuProps;
  getPopupContainer: () => HTMLElement;
};

function isInteractiveTarget(
  event: KeyboardEvent<HTMLElement> | PointerEvent<HTMLElement>,
) {
  return (
    event.target instanceof Element &&
    event.target !== event.currentTarget &&
    event.target.closest(
      'button, a, input, textarea, select, [contenteditable="true"]',
    ) !== null
  );
}

export default function SortableTab({
  className,
  draggingClassName,
  node,
  menu,
  getPopupContainer,
}: SortableTabProps) {
  const t = useShellText();
  const {
    active,
    attributes,
    isDragging,
    listeners,
    setNodeRef,
    transform,
    transition,
  } = useSortable({ id: String(node.key) });

  return (
    <Dropdown
      menu={menu}
      trigger={['contextMenu']}
      getPopupContainer={getPopupContainer}
      popupRender={(content) => <PressRipple>{content}</PressRipple>}
    >
      {cloneElement(node, {
        children: Children.map(node.props.children, (child) => {
          if (
            !isValidElement<HTMLAttributes<HTMLDivElement>>(child) ||
            child.props.role !== 'tab'
          )
            return child;
          return cloneElement(child, {
            onKeyDown: (event) => {
              // 排序期间键盘由dnd-kit负责，避免AntD方向键换焦点后Space误导航。
              if (!active && !event.defaultPrevented) child.props.onKeyDown?.(event);
            },
          });
        }),
        ref: setNodeRef,
        className: clsx(
          node.props.className,
          className,
          isDragging && draggingClassName,
        ),
        style: {
          ...node.props.style,
          transform: CSS.Translate.toString(transform),
          transition: isDragging ? 'none' : transition,
          zIndex: isDragging ? 1 : undefined,
        },
        // 不覆盖原生tab的role/tabIndex；关闭按钮和链接不能触发拖动。
        'aria-describedby': attributes['aria-describedby'],
        'aria-disabled': attributes['aria-disabled'],
        'aria-roledescription': t('可拖拽页面标签', 'Draggable page tab'),
        // 当前AntD先在tab内消费Space，需在捕获阶段交给dnd-kit。
        onKeyDownCapture: (event) => {
          node.props.onKeyDownCapture?.(event);
          if (!isInteractiveTarget(event)) listeners?.onKeyDown?.(event);
        },
        onPointerDown: (event) => {
          node.props.onPointerDown?.(event);
          if (event.button === 0 && !isInteractiveTarget(event))
            listeners?.onPointerDown?.(event);
        },
      })}
    </Dropdown>
  );
}
